import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { CheckCircle2, MapPin, Star, ArrowRight, Phone } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/8146213/pexels-photo-8146213.jpeg?auto=compress&cs=tinysrgb&w=1400";
const ROOM_IMG = "https://images.pexels.com/photos/5942742/pexels-photo-5942742.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function CityPage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [packages, setPackages] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    api.get(`/cities/${slug}`).then((r) => setCity(r.data)).catch(() => setError(true));
    api.get("/packages").then((r) => setPackages(r.data.packages || []));
    api.get(`/cities/${slug}/gallery`).then((r) => setGallery(r.data.items || [])).catch(() => setGallery([]));
  }, [slug]);

  useSeo(
    city
      ? {
          title: `Air Duct Cleaning in ${city.name}, ${city.state} | Full Indoor Air Services`,
          description: `${city.headline} — ${city.blurb} Book online for same-week service.`,
          canonical: `https://capitalindoorair.com/service-areas/${city.slug}`,
          ogImage: HERO_IMG,
        }
      : { title: "Service Area | Full Indoor Air Services" }
  );

  if (error) {
    return (
      <div className="section-pad text-center" data-testid="city-not-found">
        <h1 className="font-heading text-4xl">City not found</h1>
        <Link to="/service-areas" className="mt-6 inline-block text-forest underline">Browse all service areas</Link>
      </div>
    );
  }

  if (!city) return <div className="section-pad text-muted2">Loading…</div>;

  return (
    <div className="bg-bone" data-testid={`city-page-${city.slug}`}>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12 md:pt-24 md:pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="eyebrow mb-3 flex items-center gap-2">
              <MapPin size={12} /> {city.state === "MD" ? "Maryland" : city.state === "DC" ? "Washington DC" : "Virginia"}
            </div>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light leading-[1.04]">
              {city.headline.split(" in ")[0]} <span className="text-forest">in {city.name}, {city.state}.</span>
            </h1>
            <p className="mt-6 text-lg text-muted2 leading-relaxed max-w-xl">{city.blurb}</p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={`/book?city=${city.slug}`} className="btn-primary" data-testid="city-book-cta">Book {city.name} service <ArrowRight size={14} /></Link>
              <a href="tel:+18003737324" className="btn-ghost"><Phone size={14} /> 1-800-FRESH-AIR</a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.18em] text-muted2">
              <span className="flex items-center gap-2"><Star size={14} className="text-clay fill-clay" /> 4.9 / 5 in {city.name}</span>
              <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-forest" /> Same-week scheduling</span>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-in">
            <div className="rounded-3xl overflow-hidden card-surface">
              <img src={HERO_IMG} alt={`Air duct cleaning ${city.name}`} className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="section-pad bg-surfaceAlt" data-testid="neighborhoods-section">
        <div className="max-w-7xl mx-auto">
          <div className="eyebrow mb-3">Where we work</div>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">
            Neighborhoods we cover in {city.name}.
          </h2>
          <div className="mt-8 flex flex-wrap gap-3">
            {city.neighborhoods.map((n) => (
              <span key={n} className="card-surface px-5 py-3 text-sm" data-testid={`neighborhood-${n.toLowerCase().replace(/\s+/g, "-")}`}>
                {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Before / After Gallery */}
      <section className="section-pad" data-testid="city-gallery">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <div className="eyebrow mb-3">Before · After</div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">
                Real {city.name} homes we've serviced.
              </h2>
            </div>
            {gallery.length === 0 && (
              <p className="text-sm text-muted2 max-w-sm">Showing recent project highlights. Fresh local photos drop in as new {city.name} jobs wrap.</p>
            )}
          </div>

          {gallery.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {gallery.map((g) => (
                <div key={g.id} className="card-surface overflow-hidden" data-testid={`gallery-item-${g.id}`}>
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <img src={g.before_url} alt="Before" className="w-full h-64 object-cover" loading="lazy" />
                      <span className="absolute top-3 left-3 bg-ink/85 text-white text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full">Before</span>
                    </div>
                    <div className="relative">
                      <img src={g.after_url} alt="After" className="w-full h-64 object-cover" loading="lazy" />
                      <span className="absolute top-3 left-3 bg-forest text-white text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full">After</span>
                    </div>
                  </div>
                  {g.caption && (
                    <div className="p-5 text-sm text-muted2">{g.caption}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { id: "stock-7061333", url: "https://images.pexels.com/photos/7061333/pexels-photo-7061333.jpeg?auto=compress&cs=tinysrgb&w=800" },
                { id: "stock-8146213", url: "https://images.pexels.com/photos/8146213/pexels-photo-8146213.jpeg?auto=compress&cs=tinysrgb&w=800" },
                { id: "stock-5942742", url: "https://images.pexels.com/photos/5942742/pexels-photo-5942742.jpeg?auto=compress&cs=tinysrgb&w=800" },
                { id: "stock-32588555", url: "https://images.pexels.com/photos/32588555/pexels-photo-32588555.jpeg?auto=compress&cs=tinysrgb&w=800" },
              ].map((img) => (
                <div key={img.id} className="rounded-2xl overflow-hidden card-surface hover-lift">
                  <img src={img.url} alt={`${city.name} project`} className="w-full h-44 md:h-56 object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Packages */}
      <section className="section-pad" data-testid="city-packages">
        <div className="max-w-7xl mx-auto">
          <div className="eyebrow mb-3">Pricing in {city.name}</div>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mb-10">
            Flat-rate packages, no surprises.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className={`card-surface p-7 hover-lift ${pkg.featured ? "ring-2 ring-clay/60" : ""}`}
              >
                <h3 className="font-heading text-xl">{pkg.name}</h3>
                <p className="text-sm text-muted2 mt-1">{pkg.tagline}</p>
                <div className="font-heading text-3xl mt-4">from ${pkg.price_from}</div>
                <ul className="mt-4 space-y-2 text-sm">
                  {pkg.highlights.slice(0, 3).map((h) => (
                    <li key={h} className="flex gap-2 text-ink/80"><CheckCircle2 size={14} className="text-forest mt-0.5" />{h}</li>
                  ))}
                </ul>
                <Link to={`/book?package=${pkg.id}&city=${city.slug}`} className={`mt-5 inline-flex items-center gap-2 ${pkg.featured ? "btn-primary" : "btn-ghost"} w-full justify-center`}>
                  Book in {city.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why {city} */}
      <section className="section-pad bg-surfaceAlt">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden card-surface">
              <img src={ROOM_IMG} alt={`Indoor air in ${city.name}`} className="w-full h-[400px] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3">Why {city.name} homeowners choose us</div>
            <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">
              Local crews. National standards.
            </h2>
            <ul className="mt-8 space-y-3 text-ink/85">
              {[
                `Locally based teams familiar with ${city.name} home styles and HVAC layouts`,
                "NADCA-aligned 7-step process, every job",
                "Photo before/after report sent to you the same day",
                "Drop cloths, shoe covers, and same-day follow-up",
                "Transparent flat-rate pricing — no high-pressure upsells",
              ].map((p) => (
                <li key={p} className="flex gap-3"><CheckCircle2 size={18} className="text-forest mt-0.5 shrink-0" /><span>{p}</span></li>
              ))}
            </ul>
            <Link to={`/book?city=${city.slug}`} className="btn-primary mt-10" data-testid="city-bottom-cta">
              Book {city.name} service <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
